/**
 * Der Schalter „Mikro Auto“ im Eingabefeld (19.10.2026, Martin 09:15).
 *
 * Martin: „wichtig, wenn ich nur schreibe oder es einfach so will“ — das Mikro soll nach Antworten nicht von
 * selbst aufgehen, solange er tippt. Der Chip ist KEIN eigener Zustand: er liest und schreibt dieselbe
 * Einstellung `autoMikroNachFrage` wie der Knopf im Mikrofon-Fenster und die Einstellungen-Seite.
 * An = jede Stufe außer „aus“ (nach Frage, Jev entscheidet). Beim Ausschalten merkt sich das Gerät die letzte
 * An-Stufe (nur diese Erinnerung liegt extra, nie der Zustand selbst); beim Einschalten kommt sie zurück,
 * ohne Erinnerung gilt „nach Frage“. Wirkung: shared/mikro-nach-antwort.ts (Stufe aus ⇒ nie von selbst) und die
 * Sperre in app.ts `startPttHandsFree`. Der Mikro-Knopf und „Hey Vespera“ sind davon nie betroffen.
 */
import { loadSettings, saveSettings, autoMikroStufeFuerKonto, type AutoMikroStufe } from "./settings.js";
import { jevMoeglich, jevStufeGeschuetzt } from "./jev-konto.js";

/** Wo das Gerät die letzte An-Stufe merkt. */
export const LETZTE_STUFE_KEY = "voice-v8.automikro-letzte-stufe";

/** Ist das Mikro von selbst an? (jede Stufe außer aus) */
export function autoMikroAn(stufe: AutoMikroStufe | undefined): boolean {
  return stufe === true || stufe === "jev";
}

/** Die Stufe, die für das angemeldete Konto gilt („jev“ ohne Jev = „Nach Frage“). */
export function autoMikroGeltend(stufe: AutoMikroStufe | undefined): AutoMikroStufe | undefined {
  return autoMikroStufeFuerKonto(stufe, jevMoeglich());
}

function leseLetzte(): AutoMikroStufe {
  try {
    return localStorage.getItem(LETZTE_STUFE_KEY) === "jev" && jevMoeglich() ? "jev" : true;
  } catch {
    return true;
  }
}

function merkeLetzte(stufe: AutoMikroStufe): void {
  try {
    localStorage.setItem(LETZTE_STUFE_KEY, stufe === "jev" ? "jev" : "true");
  } catch {
    /* ohne Speicher: Vorgabe „nach Frage“ */
  }
}

/** Die Stufe nach einem Tipp auf den Chip: an → aus (Stufe merken), aus → die gemerkte Stufe (sonst nach Frage). */
export function stufeNachChipTipp(stufe: AutoMikroStufe | undefined): AutoMikroStufe {
  if (autoMikroAn(stufe)) {
    merkeLetzte(stufe as AutoMikroStufe);
    return false;
  }
  return leseLetzte();
}

const TITEL_AN = "Mikro öffnet von selbst: an – nach Fragen geht das Mikro von selbst auf. Tippen: aus";
const TITEL_AUS = "Mikro öffnet von selbst: aus – nur Tippen. Das Mikro öffnet nur per Knopf oder „Hey Vespera“. Tippen: an";

/** Den Chip auf den Stand der Einstellung bringen (Klasse, Wort, Ansage, Titel). */
export function zeigeAutoMikroChip(el: HTMLElement | null): void {
  if (!el) return;
  const an = autoMikroAn(loadSettings().autoMikroNachFrage);
  el.classList.toggle("is-an", an);
  el.setAttribute("aria-pressed", String(an));
  el.setAttribute("aria-label", `Mikro öffnet von selbst: ${an ? "an" : "aus"}`);
  el.title = an ? TITEL_AN : TITEL_AUS;
  const wort = el.querySelector(".composer-automikro-zustand");
  if (wort) wort.textContent = an ? "an" : "aus";
}

/** Ein Tipp: Einstellung umschalten, Chip sofort neu zeichnen. Gibt den neuen Zustand zurück. */
export function schalteAutoMikroChip(el: HTMLElement | null, melde: (text: string) => void): boolean {
  const jetzt = loadSettings();
  // Konto noch unbekannt und „jev“ gespeichert: der Tipp ändert nichts (lib/jev-konto.ts).
  if (jevStufeGeschuetzt(jetzt.autoMikroNachFrage)) return autoMikroAn(jetzt.autoMikroNachFrage);
  const stufe = stufeNachChipTipp(jetzt.autoMikroNachFrage);
  saveSettings({ ...jetzt, autoMikroNachFrage: stufe });
  zeigeAutoMikroChip(el);
  const an = autoMikroAn(stufe);
  melde(an ? "Mikro öffnet von selbst: an" : "Mikro öffnet von selbst: aus – nur Tippen");
  return an;
}
